import { Component, ViewChild } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { MatSnackBar } from '@angular/material/snack-bar';
import { 
  Company,
  GetStyleTemplateRequest, GetStyleTemplateResponse, GetStyleTreeRequest, GetStyleTreeResponse, 
  ProductName, SaveStyleTemplateRequest, SaveStyleTemplateResponse, SituViewWebApiClient,
  StyleCategoryNode, StyleNode, TemplateStyle, ThumbnailType, UploadStyleThumbnailRequest, UploadStyleThumbnailResponse
} from 'situview-webapi-client';
import { AuthService, BreadcrumbService, CompanyViewService, StateService } from 'src/app/services';
import { CdkDragDrop, transferArrayItem, copyArrayItem } from '@angular/cdk/drag-drop';
import { SituBaseComponent } from 'src/app/shared/situ-base-component';
import { AssetUploadComponent } from 'src/app/components';
import { MatDialog } from '@angular/material/dialog';
import { StyleTemplateCopyDialogComponent } from '../style-template-copy-dialog/style-template-copy-dialog.component';
import { FormControl } from '@angular/forms';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';

@Component({
  selector: 'style-template-edit',
  templateUrl: './style-template-edit.component.html',
  styleUrls: ['./style-template-edit.component.less']
})

export class StyleTemplateEditComponent extends SituBaseComponent {
  id: string = '';
  home: string = '';
  tag: string = '';

  data: GetStyleTemplateResponse | undefined;
  inTemplate: TemplateStyle[] | undefined;
  selectedInTemplate: TemplateStyle | undefined;
  allStyles: TemplateStyle[] | undefined;
  thumbnailData: string | undefined;
  productName = ProductName;

  isTemplateModified: boolean = false;
  showAllCompanies: boolean = false;

  showFacadeBuilder: boolean = false;
  facadeBuilderSingleStorey: boolean = true;
  facadeSceneryLayers: Record<number, LayerInfo> = {}

  searchTerm = "";

  //bridgeCompanyId: string = 'c1fa0489-6906-4ddc-b384-ae041454f861';
  bridgeCompanyId: string = '6a452cb9-12dd-434a-b27a-d5c9ebc1c632';

  @ViewChild(AssetUploadComponent) assetUploadComponent: AssetUploadComponent | undefined;

  triggerChildFileUpload() {
    if (this.assetUploadComponent) {
      this.assetUploadComponent.triggerFileUpload();
    }
  }

  private _loading = false;
  public get loading(): boolean { return this._loading; }

  public set loading(val: boolean) {
    this._loading = val;
    this.breadcrumbService.setIsLoading(val);
  }

  constructor(
    authService: AuthService,
    stateService: StateService,
    private route: ActivatedRoute,
    private router: Router,
    private snackBar: MatSnackBar,
    private breadcrumbService: BreadcrumbService,
    private situViewApiClient: SituViewWebApiClient,
    private dialog: MatDialog,
    private companyViewService: CompanyViewService) {
    super(authService, stateService);
    this.id = this.route.snapshot.params['id'];
    this.home = this.route.snapshot.queryParams['home'];
    this.tag = this.route.snapshot.queryParams['tag'];
  }

  protected onSelectedCompanyChange(): void {
    this.load();
    this.loadStyles();
  }

  ngOnInit(): void {
    super.ngOnInit();
    this.companyViewService.LoadCompanies()
      .then((companies: Company[]) => {
        this.allCompanies = companies;
      });
    this._subscriptions.push(this.searchCategoryDataField.valueChanges
      .pipe(
        debounceTime(300),
        distinctUntilChanged()
      )
      .subscribe(value => {
        this.doCategoryDataFilter(value);
      }));
  }

  allCompanies: Company[] = [];
  getCompanyLogo(companyId: string) {
    return this.allCompanies.find(x => x.id === companyId)?.logoUrl;
  }

  getCompanyName(companyId: string) {
    if (companyId === this.bridgeCompanyId) return "Bridge";
    return this.allCompanies.find(x => x.id === companyId)?.name;
  }

  getThemeDisplayName(themeId: string, name: string) {
    if (!this.categoryData) return name;
    for (const category of this.categoryData) {
      const matchingStyle = category?.styles?.find((style: any) => style.id === themeId);
      if (matchingStyle) {
        return matchingStyle.displayName;
      }
    }
    return name;
  }

  drop(event: CdkDragDrop<StyleNode[]>): void {
    if (event.container.id === "inRangeContainer" && event.previousContainer.id === "inRangeContainer") {
      transferArrayItem(
        event.previousContainer.data,
        event.container.data,
        event.previousIndex,
        event.currentIndex
      );

      this.checkIfTemplateModified();
    } else if (event.container.id === "inRangeContainer") {
      // 1. Extract the item you're about to transfer
      const draggedItem: StyleNode = event.previousContainer.data[event.previousIndex];
  
      // 2. Convert that item into the new data format
      const newData = this.convertToTemplateStyle(draggedItem);
  
      // Transfer the original item to the destination container
      copyArrayItem(
        event.previousContainer.data,
        event.container.data,
        event.previousIndex,
        event.currentIndex
      );
  
      // Replace the item in the destination container with the converted data
      event.container.data[event.currentIndex] = newData as unknown as StyleNode; // Type casting if required
  
      this.checkIfTemplateModified();
    }
  }

  onAssetLoad(assetBase64: string) {
    if (assetBase64) {
      this.thumbnailData = assetBase64;
    }
  }

  duplicateTemplate() {
    this.dialog.open(StyleTemplateCopyDialogComponent, {
      width: '60vw',
      height: '60vh',
      data: { 
        existingName: this.data?.name,
        styles: this.inTemplate?.map(x => x.id!)
       }
    });
  }

  hideTemplate(hide: boolean) {
    let request = new SaveStyleTemplateRequest();
    request.productName = this.data?.productName;
    request.isHidden = hide;
    if (this.data?.isHidden !== hide) {
      this.saveStyleTemplate(request);
    }
  }

  setProduct(productName: ProductName) {
    let request = new SaveStyleTemplateRequest();
    request.productName = productName === this.data?.productName ? undefined : productName;
    request.isHidden = this.data?.isHidden;
    if (this.data?.productName !== request.productName) {
      this.saveStyleTemplate(request);
    }
  }

  cancelThumbnailUpload() {
    this.thumbnailData = undefined;
  }
  
  saveThumbnailUpload() {
    if (!this.data?.id || !this.thumbnailData) {
      return;
    }

    let request = new UploadStyleThumbnailRequest();
    request.companyId = this.id;
    request.id = this.data.id;
    request.thumbnail = this.thumbnailData.split(',')[1];
    request.thumbnailType = ThumbnailType.StyleTemplate;

    this.situViewApiClient.uploadStyleThumbnail(request)
      .then((response: UploadStyleThumbnailResponse) => {
        if (response.success) {
          this.data!.thumbnailUrl = response.thumbnailUrl;
          this.thumbnailData = undefined;
          this.snackBar.open(`Thumbnail successfully updated!`, 'OK', { duration: 2000 });
        } else {
          this.snackBar.open(`Could not save thumbnail. Refresh page.`, 'OK', { duration: 2000 });
        }
      })
      .catch(x => {
        this.snackBar.open(`Failed to upload thumbnail`, 'OK', { duration: 2000 });
      });
  }

  onClearTemplate(templateStyleId: string) {
    this.inTemplate = this.inTemplate?.filter(x => x.id !== templateStyleId);
    if (templateStyleId === this.selectedInTemplate?.id)  this.selectedInTemplate = undefined;
    this.checkIfTemplateModified();
  }
  
  onSwapTemplate(templateStyleId: string) {
    if (!templateStyleId || !this.inTemplate) return;
    if (this.selectedInTemplate) {
      let template = this.inTemplate.find(x => x.id === templateStyleId);
      this.selectedInTemplate = template?.id === this.selectedInTemplate.id ?
        undefined : template;
    } else {
      this.selectedInTemplate = this.inTemplate.find(x => x.id === templateStyleId);
    }
  }
  
  onSwapTemplateSelected(templateStyleId: string) {
    if (!templateStyleId || !this.inTemplate || !this.categoryData || this.inTemplate.map(x => x.id).includes(templateStyleId)) return;
    this.categoryData.forEach(category => {
      if (category?.styles && category.styles.findIndex(x => x.id === templateStyleId) !== -1) {
        let style =  category.styles.find(x => x.id === templateStyleId);
        if (!style) return;
        const index = this.inTemplate!.findIndex(item => item.id === this.selectedInTemplate?.id);
        if (index !== -1) {
          const newData = this.convertToTemplateStyle(style);
          this.inTemplate![index] = newData;
          this.checkIfTemplateModified();
        }
      }
    });
  }

  isInTemplate(templateStyleId: string): boolean {
    if (!this.inTemplate) return false;
    return this.inTemplate.findIndex(x => x.id === templateStyleId) !== -1;
  }

  getCategoryName(categoryId: string): string {
    if (!this.categoryData) return "";
    return this.categoryData?.find(x => x.id === categoryId)?.name ?? "";
  }
  
  categoryIsSingle(categoryId: string): boolean {
    if (!this.categoryData) return false;
    return this.categoryData?.find(x => x.id === categoryId)?.isSingleSelect ?? false;
  }

  onInTemplateClick(templateStyle: TemplateStyle) {
    if (!templateStyle) return;
    this.selectedInTemplate = this.selectedInTemplate?.id === templateStyle.id ?
      undefined : templateStyle;
  }

  clearChanges() {
    this.load();
    this.isTemplateModified = false;
  }

  refreshStyleData() {
    this.loadStyles();
  }

  checkIfTemplateModified() {
    this.isTemplateModified = this.data!.styles!.length !== this.inTemplate!.length ||
      !this.data!.styles!.every((style, index) => this.inTemplate![index]?.id === style.id);
  }

  saveStyleTemplate(req?: SaveStyleTemplateRequest) {
    if (!this.id || !this.inTemplate) {
      this.snackBar.open(`Error!`, 'OK', { duration: 2000 });
      return; 
    } 

    let request = req ? req : new SaveStyleTemplateRequest();
    request.styleTemplateId = this.id;
    if (!req) request.styles = this.inTemplate.filter(x => x?.id).map(x => x.id) as string[];

    this.situViewApiClient.saveStyleTemplate(request)
      .then((x: SaveStyleTemplateResponse) => {
        if (x.success) {
          this.snackBar.open(`${this.data?.name} style template updated!`, 'OK', { duration: 2000 });
          if (!req) {
            this.router.navigate([`admin/style-template`]);
          } else {
            this.data!.isHidden = req.isHidden;
            this.data!.productName = req.productName;
          }
        } else {
          this.snackBar.open(`Error: ${x.errorMessages}!`, 'OK', { duration: 2000 });
        }
      })
      .catch((err: any) => {
        this.snackBar.open(`Error!`, 'OK', { duration: 2000 });
      });
  }

  private load(): void {
    let request = new GetStyleTemplateRequest();
    request.styleTemplateId = this.id;

    this.situViewApiClient.getStyleTemplate(request)
      .then((response: GetStyleTemplateResponse) => {
        if (response.success) {
          this.breadcrumbService.setParam('styleTemplateName', response?.name);
          this.data = response;
          this.inTemplate = [...response.styles!].sort((a, b) => a.order! - b.order!);
        } else {
          this.snackBar.open(`Could not fetch the style template. Refresh Page.`, 'OK', { duration: 2000 });
        }
      })
      .catch((err: any) => {
        this.snackBar.open(`Could not fetch the style template. Refresh Page.`, 'OK', { duration: 2000 });
      });
  };
  
  searchCategoryDataField = new FormControl();
  categoryData: StyleCategoryNode[] | undefined;
  filteredCategoryData: StyleCategoryNode[] | undefined;
  private loadStyles(): void {
    let request = new GetStyleTreeRequest();
    request.companyId = this.selectedCompany?.id;
    request.showAll = true;
  
    this.situViewApiClient.getStyleTree(request)
      .then((response: GetStyleTreeResponse) => {
        if (response.success && response.categories) {
          this.categoryData = response.categories
            .filter(item => !!item)
            .sort((a, b) => {
              const aCompanyId = a.companyId ?? '';
              const bCompanyId = b.companyId ?? '';
              const selectedCompanyId = this.selectedCompany?.id ?? '';
  
              if (aCompanyId === selectedCompanyId) return -1;
              if (bCompanyId === selectedCompanyId) return 1;
  
              if (aCompanyId === this.bridgeCompanyId) return -1;
              if (bCompanyId === this.bridgeCompanyId) return 1;
  
              const aName = a.name ?? '';
              const bName = b.name ?? '';
              return aName.localeCompare(bName);
            });
  
            this.categoryData.forEach(category => {
            category.styles = category.styles?.sort((a, b) => {
              const aDisplayName = a?.displayName ?? '';
              const bDisplayName = b?.displayName ?? '';
              return aDisplayName.localeCompare(bDisplayName);
            }) ?? [];
          });
          this.filteredCategoryData = [...this.categoryData];
        } else {
          this.snackBar.open(`Could not fetch the style templates. Refresh Page.`, 'OK', { duration: 2000 });
        }
      })
      .catch((err: any) => {
        this.snackBar.open(`Could not fetch the style templates. Refresh Page.`, 'OK', { duration: 2000 });
      });
  };

  doCategoryDataFilter = (value: string) => {
    if (!this.categoryData) {
      this.filteredCategoryData = [];
    } else if (this.showFacadeBuilder) {
      const tempData = JSON.parse(JSON.stringify(this.categoryData));
      this.filteredCategoryData = tempData
        .filter((category: StyleCategoryNode) => {
          if (!category) return false;
  
          category.styles = category.styles?.filter((style) => {
            return style.facadeSceneryLayerFileNames && style.facadeSceneryLayerFileNames.length > 0;
          }) ?? [];
  
          return category.styles.length > 0;
        });
    } else if (value) {
      let searchTerm = value.trim().toLowerCase();
  
      const tempData = JSON.parse(JSON.stringify(this.categoryData));
      this.filteredCategoryData = tempData
        .filter((category: StyleCategoryNode) => {
          if (!category) return false;
  
          const matchesCategory = category.name?.toLowerCase().includes(searchTerm) ||
            category.description?.toLowerCase().includes(searchTerm);
  
          const filteredStyles = category.styles?.filter((style) => {
            return style.displayName?.toLowerCase().includes(searchTerm) ||
              style.themeName?.toLowerCase().includes(searchTerm);
          }) ?? [];
  
          if (matchesCategory) {
            category.styles = category.styles ?? [];
            return true;
          }
  
          if (filteredStyles.length > 0) {
            category.styles = filteredStyles;
            return true;
          }

          return false;
        });
    } else {
      this.filteredCategoryData = [...this.categoryData!]
    }
  };

  private convertToTemplateStyle(node: StyleNode): TemplateStyle {
    let newData = new TemplateStyle();
    newData.id = node.id;
    newData.styleCategoryId = node.styleCategoryId;
    newData.name = node.themeName;
    newData.thumbnailUrl = node.thumbnailUrl;
    newData.facadeSceneryLayerFileNames = node.facadeSceneryLayerFileNames;
    newData.companyId = node.companyId;
    return newData;
  }

  enableFacadeBuilder() {
    this.showFacadeBuilder = !this.showFacadeBuilder;

    this.doCategoryDataFilter('');

    this.buildFacadeLayers();
  }

  buildFacadeLayers() {

    if (!this.showFacadeBuilder) {
      return;
    }

    var urlRoot = 'https://cdn.situsystems.com/images/facade_scenery_preview';
    var stories = this.facadeBuilderSingleStorey ? 'single' : 'double';

    var previousLayers = this.facadeSceneryLayers;
    this.facadeSceneryLayers = {
      90: { src: `${urlRoot}/${stories}/facade_10_grass_lawn_shortgreen.webp`, classes: ['unset'] },
      50: { src: `${urlRoot}/${stories}/facade_50_home.webp`, classes: ['home'] },
      40: { src: `${urlRoot}/${stories}/facade_60_fence_fence_wooden01.webp`, classes: ['unset'] },
      10: { src: `${urlRoot}/${stories}/facade_90_scene_blank.webp`, classes: ['unset'] },
    };

    if (this.inTemplate) {
      for (const style of this.inTemplate) {
        if (style.facadeSceneryLayerFileNames) {
          for (const facadeSceneryLayerFileName of style.facadeSceneryLayerFileNames) {
            var zIndex = 100 - Number(facadeSceneryLayerFileName.split('_')[1]);
            var src = `${urlRoot}/${stories}/${facadeSceneryLayerFileName}`;

            var classes: string[] = [];
            if (previousLayers[zIndex]?.src && previousLayers[zIndex].src !== src) {
              this.facadeSceneryLayers[zIndex + 1] = previousLayers[zIndex];
              classes.push('hidden');
            }

            this.facadeSceneryLayers[zIndex] = { src, classes };
          }
        }
      }

    }
  }

  onFacadeSceneryLayerLoadComplete(key: string) {
    var layerIndex = Number(key);
    this.facadeSceneryLayers[layerIndex].classes = this.facadeSceneryLayers[layerIndex].classes.filter(x => x !== 'hidden');

    layerIndex++;
    delete this.facadeSceneryLayers[layerIndex];
  }

  switchFacadeBuilderStorey() {
    this.facadeBuilderSingleStorey = !this.facadeBuilderSingleStorey;

    this.buildFacadeLayers();
  }

  selectSceneryStyle(style: StyleNode) {
    var styleCategoryName = this.getCategoryName(style.styleCategoryId!);
    var newData = this.convertToTemplateStyle(style);
    var index = this.inTemplate!.findIndex(item => this.getCategoryName(item.styleCategoryId!) === styleCategoryName);
    if (index >= 0) {
      if (this.inTemplate![index].id === newData.id) {
        this.inTemplate!.splice(index, 1)
      } else {
        this.inTemplate![index] = newData;
      }
    } else {
      this.inTemplate!.push(newData);
    }

    this.buildFacadeLayers();

    this.checkIfTemplateModified();
  }

  showMessage(message: string) {
    this.snackBar.open(message, 'Dismiss', {
      duration: 3000
    });
  }
}

interface LayerInfo {
  src: string;
  classes: string[];
}